// services/notificationService.ts
import type { CSSProperties } from 'react'

export type NotificationSeverity = 'success' | 'error' | 'warning' | 'info'

export interface NotificationOptions {
  /** Thời gian chờ trước khi tự đóng (ms). */
  duration?: number
  position?: {
    vertical: 'top' | 'bottom'
    horizontal: 'left' | 'center' | 'right'
  }
  /** Style snackbar (vd. maxWidth hẹp để toast thông báo xuống nhiều dòng) */
  style?: CSSProperties
  preventDuplicate?: boolean
  /** Không tự đóng — chỉ đóng khi bấm nút close. */
  persist?: boolean
  action?: {
    label: string
    onClick: () => void
  }
}

export type NotificationHandler = (
  message: string,
  severity?: NotificationSeverity,
  options?: NotificationOptions,
) => void

class NotificationService {
  private handler: NotificationHandler | null = null

  /**
   * Đăng ký handler toàn cục — gọi một lần từ NotificationProvider sau khi MUI/notistack sẵn sàng.
   */
  setHandler(handler: NotificationHandler): void {
    this.handler = handler
  }

  show(
    message: string,
    severity: NotificationSeverity = 'info',
    options?: NotificationOptions,
  ): void {
    try {
      if (this.handler) {
        this.handler(message, severity, options)
      } else {
        this.fallbackNotification(message, severity)
      }
    } catch (error) {
      console.warn('Notification failed:', error)
      this.fallbackNotification(message, severity)
    }
  }

  success(message: string, options?: NotificationOptions): void {
    this.show(message, 'success', options)
  }

  error(message: string, options?: NotificationOptions): void {
    this.show(message, 'error', options)
  }

  warning(message: string, options?: NotificationOptions): void {
    this.show(message, 'warning', options)
  }

  info(message: string, options?: NotificationOptions): void {
    this.show(message, 'info', options)
  }

  isAvailable(): boolean {
    return this.handler !== null
  }

  clearHandler(): void {
    this.handler = null
  }

  private fallbackNotification(message: string, severity: NotificationSeverity): void {
    const styles = {
      success: 'color: #4caf50; font-weight: bold;',
      error: 'color: #f44336; font-weight: bold;',
      warning: 'color: #ff9800; font-weight: bold;',
      info: 'color: #2196f3; font-weight: bold;',
    }
    console.log(`%c[${severity.toUpperCase()}] ${message}`, styles[severity] || styles.info)
  }
}

export const notificationService = new NotificationService()

export default notificationService
